import { createAsyncThunk } from '@reduxjs/toolkit';
import { vedleggMaFjernes } from 'src/util/attacehmentUtils';
import { getLanguagesInDefinition } from 'src/util/getLanguagesInDefinition';
import { getHnLanguageLocaleFromString, getPreferredLanguage, isValidPortalLanguage, LanguageLocales } from 'src/util/portalLanguage-utils';
import { redirectToMeldingssenter } from 'src/util/redirectUtils';

import type {
  CreateSkjemasvarRequest,
  Metadata,
  TextMessage,
  UpdateSkjemasvarRequest,
  UpdateSkjemasvarResponse,
} from '../../types/MinHelseEntities';
import type { AppDispatch, RootState } from '../reducers';
import type { ValueSet, QuestionnaireItem, QuestionnaireResponse, Questionnaire } from 'fhir/r4';
import type { RedirectParams } from 'src/types/url';

import { type HelsenorgeProxyError, get, post, put } from '@helsenorge/framework-utils/hn-proxy-service';
import { setAllowNavigation } from '@helsenorge/framework-utils/hn-user';
import { languageChanged } from '@helsenorge/framework-utils/language';
import { type OrgenhetHierarki, ProcedureStatusCodes, setSkjemaDefinitionAction } from '@helsenorge/refero';

import { type LagreSkjemaDonePayload, shouldShowBeforeUnload } from './actions';
import {
  createQuestionnaireResponse,
  createSavePayload,
  generateQuestionnaireResponseData,
  getQuestionnaireInLanguage,
  gotoMinHelse,
  gotoUrl,
  isHelsenorgeProxyError,
  isNewDocument,
  redirectTilLagretSkjemaPaaDokumenter,
  removeAttachmentsIfNeeded,
  updateUrlAndFetchMessage,
} from './actionUtils';
import { type ProxyErrorResponse, transformFetchErrors } from './errorHandling';
import { getQueryParams, oppdaterQuestionnaireResponseSomNyReponseHvisGjenbrukes, toRelativeUrl } from '../../util';
import { FULLFORING_AV_SKJEMA_FEIL, LAGRING_AV_SKJEMA_FEIL, logSkjemaLagringsFeil } from '../../util/logUtil';
import { redirectEtterFullfortSkjema } from '../../util/veileder';

type ValueSetResponse = {
  ValueSet: { System: string; Code: string; Display: string }[];
};
type LastMottakereResponse = { Orgenhet: OrgenhetHierarki[] };

export const sendInnSkjema = createAsyncThunk<
  { samtaleGuid: string | undefined },
  {
    timeSkjemaLoadedForUser: number;
    automatiskLagring: boolean;
    isMicrowebStep: boolean | undefined;
    isMicrowebOppgave: boolean | undefined;
    onCompleteMicroweb?: (formData: Record<string, string | undefined>) => void;
    documentGuid?: string;
    returUrl?: string;
  },
  { state: RootState; dispatch: AppDispatch; rejectValue: string | ProxyErrorResponse }
>(
  'sendInnSkjema',
  async (
    { timeSkjemaLoadedForUser, automatiskLagring, isMicrowebStep, isMicrowebOppgave, onCompleteMicroweb, documentGuid, returUrl },
    { getState, rejectWithValue, fulfillWithValue }
  ) => {
    const isNewDoc = isNewDocument(getState());

    const qr = JSON.stringify({
      ...createQuestionnaireResponse(getState()),
      status: ProcedureStatusCodes.COMPLETED,
    });
    try {
      const payload = createSavePayload(qr, getState(), automatiskLagring, isMicrowebStep, documentGuid);
      const skalMicrowebStepOverskriveDokument = isMicrowebStep && documentGuid && documentGuid !== '';
      const harDocumentGuidPayload = Object.prototype.hasOwnProperty.call(payload, 'DokumentGuid');

      let sendFn = harDocumentGuidPayload && !isNewDoc ? put : post;

      if (skalMicrowebStepOverskriveDokument && harDocumentGuidPayload) {
        sendFn = put;
      }

      const response = await sendFn<UpdateSkjemasvarResponse, CreateSkjemasvarRequest | UpdateSkjemasvarRequest>(
        'skjemainternal',
        'api/v1/Skjemasvar',
        payload
      );
      if (!response) return rejectWithValue('ingen respons fra server');

      const queryParams = getQueryParams();

      if (response.SamtaleGuid && !isNewDoc && !queryParams.has('redirect')) {
        return fulfillWithValue({ samtaleGuid: response.SamtaleGuid, dokumentGuid: response.DokumentGuid });
      } else {
        const redirectParams: RedirectParams = {
          redirectUrl: queryParams.get('redirect') ?? undefined,
          contextParameter: queryParams.get('contextParameter') ?? undefined,
          documentGuid: response.DokumentGuid,
          returnUrl: queryParams.get('returnUrl') ?? undefined,
          taskId: queryParams.get('taskId') ?? undefined,
        };

        redirectEtterFullfortSkjema(
          getState().refero.form.FormDefinition,
          getState().refero.form.FormData,
          redirectParams,
          isMicrowebStep,
          isMicrowebOppgave,
          onCompleteMicroweb,
          () => redirectTilLagretSkjemaPaaDokumenter(response.DokumentGuid),
          redirectParams.taskId,
          response.SamtaleGuid,
          returUrl
        );
        return fulfillWithValue({ samtaleGuid: undefined });
      }
    } catch (error) {
      const err = error as HelsenorgeProxyError;
      const errorInfo = transformFetchErrors(err);
      // Logger etter dispatch in case en gang logger metoden feiler så får
      // fortsatt bruker en feilmelding
      logSkjemaLagringsFeil(FULLFORING_AV_SKJEMA_FEIL, err.correlationId, err.response?.code, err.statusCode, timeSkjemaLoadedForUser);
      return rejectWithValue(errorInfo);
    }
  }
);

export const lagreSkjema = createAsyncThunk<
  LagreSkjemaDonePayload,
  { timeSkjemaLoadedForUser: number; automatiskLagring: boolean; dokumentGuidMicroweb?: string; isMicrowebStep?: boolean },
  {
    state: RootState;
    dispatch: AppDispatch;
    rejectValue: string | ProxyErrorResponse;
  }
>(
  'lagreSkjema',
  async (
    { timeSkjemaLoadedForUser, automatiskLagring, dokumentGuidMicroweb, isMicrowebStep },
    { dispatch, getState, rejectWithValue, fulfillWithValue }
  ) => {
    try {
      const globalState = getState();
      const questionnaireResponse = createQuestionnaireResponse(globalState);
      const skalMicrowebStepOverskriveDokument = isMicrowebStep && dokumentGuidMicroweb && dokumentGuidMicroweb !== '';

      oppdaterQuestionnaireResponseSomNyReponseHvisGjenbrukes(questionnaireResponse, globalState.refero.ui.userMetadata);
      const qr = JSON.stringify(questionnaireResponse);
      const payload = createSavePayload(qr, globalState, automatiskLagring, isMicrowebStep, dokumentGuidMicroweb);
      let lagreFn = Object.prototype.hasOwnProperty.call(payload, 'DokumentGuid') && !isNewDocument(globalState) ? put : post;

      // Merk: Microwebprosessflyt bryr seg ikke om isNewDocument sjekken, siden dokumentGuidMicroweb staten er fra prosessflyt og ikke i globalsate
      // Dette forutsetter at dokumentGuidMicroweb blir tatt vare på frem/tilbake i prosessflyten ( noe den gjør nå )
      if (skalMicrowebStepOverskriveDokument && Object.prototype.hasOwnProperty.call(payload, 'DokumentGuid')) {
        lagreFn = put;
      }

      const response = await lagreFn<UpdateSkjemasvarResponse, CreateSkjemasvarRequest | UpdateSkjemasvarRequest>(
        'skjemainternal',
        'api/v1/Skjemasvar',
        payload
      );

      if (!response) return rejectWithValue('ingen respons fra server');
      const resonseQr: QuestionnaireResponse = JSON.parse(response?.QuestionnaireResponse);
      const currentQuestionnaire =
        globalState.refero.ui.questionnaires.filter(x => `Questionnaire/${x.id}` === questionnaireResponse.questionnaire)[0] ||
        globalState.refero.ui.questionnaires[0];

      dispatch(
        setSkjemaDefinitionAction({
          questionnaire: currentQuestionnaire,
          questionnaireResponse: resonseQr,
        })
      );

      return fulfillWithValue({ dokumentGuid: response.DokumentGuid, samtaleGuid: response.SamtaleGuid });
    } catch (error) {
      const err = error as HelsenorgeProxyError;
      const errorInfo = transformFetchErrors(err);
      if (isHelsenorgeProxyError(err)) {
        logSkjemaLagringsFeil(
          LAGRING_AV_SKJEMA_FEIL,
          err.correlationId || null,
          err.response?.code,
          err.statusCode,
          timeSkjemaLoadedForUser
        );
      }
      return rejectWithValue(errorInfo);
    }
  }
);

export const loadValueSet = async (
  searchString: string,
  item: QuestionnaireItem,
  successCallback: (valueSet: ValueSet) => void,
  errorCallback: (error: string) => void
): Promise<void> => {
  try {
    const response = await get<ValueSetResponse>('skjemainternal', 'api/v1/ValueSet/$expand', {
      url: item.answerValueSet || '',
      filter: searchString,
    });
    if (!response) {
      return;
    }
    const valueSet: ValueSet = {
      resourceType: 'ValueSet' as const,
      status: 'draft',
      compose: {
        include: [
          {
            system: response.ValueSet.length > 0 ? response.ValueSet[0].System : '',
            concept: response.ValueSet.map(x => {
              return {
                code: x.Code,
                display: x.Display,
              };
            }),
          },
        ],
      },
    };
    successCallback(valueSet);
  } catch (error) {
    errorCallback(error as string);
  }
};
export const lastMottakere = async (
  skjemaTekniskNavn: string,
  successCallback: (receivers: OrgenhetHierarki[]) => void,
  errorCallback: () => void
): Promise<void> => {
  try {
    const response = await get<LastMottakereResponse>('skjemainternal', `api/v1/OrgEnhetHierarki`, {
      skjemaNavn: skjemaTekniskNavn,
    });
    if (response) {
      successCallback(response?.Orgenhet);
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (_error) {
    errorCallback();
  }
};

export const setMainQuestionnaire = createAsyncThunk<
  void,
  {
    questionnaires: Questionnaire[];
    questionnaireResponse?: string | undefined;
    metadata: Metadata;
    availableLanguages?: Array<LanguageLocales>;
  },
  {
    dispatch: AppDispatch;
    state: RootState;
  }
>(
  'setMainQuestionnaire',
  async ({ questionnaires, metadata, questionnaireResponse, availableLanguages }, { dispatch, rejectWithValue, getState }) => {
    const isMicroweb = getState().microweb.isMicroweb || false;
    const skjemadata = getState().microweb.skjemadata;

    const languages = availableLanguages || getState().refero.ui.availableLanguages || getLanguagesInDefinition(questionnaires);

    const preferredLanguage = getPreferredLanguage(languages);
    if (isValidPortalLanguage(preferredLanguage) && preferredLanguage !== LanguageLocales.INGEN) {
      const frameworkLanguage = getHnLanguageLocaleFromString(preferredLanguage);
      if (frameworkLanguage) {
        dispatch(languageChanged(frameworkLanguage));
      }
    }
    const questionnaireInLanguage = getQuestionnaireInLanguage(questionnaires, preferredLanguage);

    const questionnaire = questionnaireInLanguage || questionnaires[0];

    if (!isMicroweb) {
      updateUrlAndFetchMessage(questionnaire);
    }
    try {
      const qr = generateQuestionnaireResponseData(questionnaireResponse, questionnaire, isMicroweb, skjemadata);
      removeAttachmentsIfNeeded(vedleggMaFjernes(metadata, isMicroweb), qr, questionnaire);
      dispatch(setSkjemaDefinitionAction({ questionnaire, questionnaireResponse: qr }));
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const abortSkjema = createAsyncThunk<
  void,
  undefined,
  {
    dispatch: AppDispatch;
    state: RootState;
  }
>('skjemautfyller/abortSkjema', async (_, { dispatch, getState }) => {
  dispatch(shouldShowBeforeUnload({ show: false }));
  const returUrl = decodeURIComponent(getState().refero.ui.returUrl || '');
  if (!getState().microweb.isMicroweb) {
    await setAllowNavigation(true);
  }
  if (returUrl) {
    gotoUrl(toRelativeUrl(returUrl));
  } else if (getState().refero.ui.dokumentGuid) {
    redirectTilLagretSkjemaPaaDokumenter();
  } else {
    gotoMinHelse();
  }
});

export const redirectAfterSave = createAsyncThunk<
  void,
  undefined,
  {
    dispatch: AppDispatch;
    state: RootState;
    rejectValue: undefined | TextMessage;
  }
>('skjemautfyller/redirectAfterSave', async (_, { dispatch, getState }) => {
  dispatch(shouldShowBeforeUnload({ show: false }));

  const samtaleGuid = getState().refero.ui.samtaleGuid;
  const returUrl = decodeURIComponent(getState().refero.ui.returUrl || '');

  if (samtaleGuid) {
    redirectToMeldingssenter(samtaleGuid || '');
  } else if (returUrl) {
    const returnUrlRelative = toRelativeUrl(returUrl);
    gotoUrl(returnUrlRelative);
  } else {
    redirectTilLagretSkjemaPaaDokumenter();
  }
});
